import React, { useEffect, useRef } from "react";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { Tooltip } from "antd";
import Image from "@components/item/Image";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { POST_APPLE_SIGNIN_URL } from "@libs/apiUrl";
import { useNavigate } from "react-router-dom";

const AppleButton = styled.button({
  display: "flex",
});

const tooltipStyle = {
  backgroundColor: "#FF4808",
  borderRadius: "2px ",
  padding: "0.2rem 0.8rem",
  height: "auto",
  minHeight: "auto",
  fontSize: "1.2rem",
  fontFamily: "Pretendard",
};

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: any) => void;
        signIn: () => Promise<any>;
      };
    };
  }
}

const AppleLoginButton = ({ recentLogin }: { recentLogin: string | null }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const scriptLoaded = useRef(false);

  const { mutate } = useMutation({
    mutationFn: async (id_token: string) => {
      const { data, headers } = await axios.post(
        POST_APPLE_SIGNIN_URL,
        { id_token, role: "BRAND" },
        { withCredentials: true },
      );
      return { data, headers };
    },
    onSuccess: (res) => {
      const { data } = res;
      const responseData = data?.data;

      if (responseData?.access_token) {
        // 로그인 성공 (기가입 또는 자동 회원 생성)
        localStorage.setItem("accessToken", responseData.access_token);
        localStorage.setItem("loginType", "APPLE");
        localStorage.setItem("isLoggedIn", "true");
        navigate("/");
      } else if (responseData?.link_token) {
        // 계정 연결 필요 (이미 같은 이메일로 가입된 계정 존재)
        alert("이미 해당 이메일로 가입된 계정이 있습니다. 기존 계정으로 로그인해주세요.");
      }
    },
    onError: (error) => {
      console.error("Apple 로그인 실패:", error);
    },
  });

  useEffect(() => {
    if (scriptLoaded.current) return;
    scriptLoaded.current = true;

    const script = document.createElement("script");
    script.src =
      "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  const handleAppleLogin = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!window.AppleID) return;

    try {
      window.AppleID.auth.init({
        clientId: process.env.REACT_APP_APPLE_REST_API_KEY || "",
        scope: "name email",
        redirectURI:
          process.env.REACT_APP_APPLE_REDIRECT_URI || window.location.origin,
        usePopup: true,
      });

      const response = await window.AppleID.auth.signIn();

      if (response?.user?.name) {
        localStorage.setItem("apple_first_name", response.user.name.firstName);
        localStorage.setItem("apple_last_name", response.user.name.lastName);
      }

      if (response?.user?.email) {
        localStorage.setItem("apple_email", response.user.email);
      }

      if (response?.authorization?.id_token) {
        mutate(response.authorization.id_token);
      }
    } catch (error) {
      console.error("Apple 로그인 실패:", error);
    }
  };

  return (
    <Tooltip
      title={recentLogin === "APPLE" ? "최근 로그인" : ""}
      placement="top"
      open={recentLogin === "APPLE"}
      color="#FF4808"
      overlayInnerStyle={tooltipStyle}>
      <AppleButton type="button" onClick={handleAppleLogin}>
        <Image
          src={theme.image.apple_icon}
          width="5.2rem"
          height="5.2rem"
        />
      </AppleButton>
    </Tooltip>
  );
};

export default AppleLoginButton;
